import { ButtonV2, TooltipV2, Search, Dropdown, Dialog } from '@cimplico/components';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/20/solid';
import { useDispatch, useSelector } from 'react-redux';
import { selectCollapseAll, selectShowHidden } from '../store/selectors';
import { setCollapseAll, setShowHidden } from '../store/slice';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import { WorkpaperRoleEnum } from '../../../../types/workpaper';
import { Fragment, useState } from 'react';
import ResetFormConfirmation from './ResetFormConfirmation';
import { Calculation } from '../../../../types/worksheet';
import { useRefreshWorksheetCalculation } from '../../../../queries/use-refresh-worksheet-calculation';
import { asyncToast, ToastDoneFunction, toastNotification } from '../../../../components/toast/Toast';
// import { Document, Page, View, Text, pdf } from '@react-pdf/renderer';
// import Property from '../export/Property';
// import CustomWorksheetSchema from '../export/CustomWorksheetSchema';
import { useIntercom } from 'react-use-intercom';

type FormActionsProps = {
    worksheetId: string;
    calculation: Calculation;
    name: string;
    search?: string;
    onSearch: (search?: string) => void;
};

function FormActions({ worksheetId, calculation, name, search, onSearch }: FormActionsProps): JSX.Element {
    const dispatch = useDispatch();
    const collapseAll = useSelector(selectCollapseAll);
    const showHidden = useSelector(selectShowHidden);
    const { isAdmin } = useIsFirmRole();
    const { role, readOnly, id: workpaperId } = useWorkpaperContext();
    const [reset, setReset] = useState(false);
    const [exportError, setExportError] = useState(false);
    const { trackEvent } = useIntercom();
    const refreshQuery = useRefreshWorksheetCalculation(worksheetId, calculation.id);

    const disableResetAction = !isAdmin && role !== WorkpaperRoleEnum.APPROVER && role !== WorkpaperRoleEnum.REVIEWER;

    function handleSearch(val: string): void {
        if (val === search) {
            return;
        }

        // Set search state back to undefined if search query is an empty string
        if (val === '') {
            onSearch(undefined);
        } else {
            onSearch(val);

            // Expand schema form if it is not expanded to show search results
            if (collapseAll !== false) {
                dispatch(setCollapseAll(false));
            }
        }
    }

    function refresh(): void {
        refreshQuery.mutate('', {
            onError: (res) => {
                toastNotification('error', 'Unable to refresh calculation', res?.response?.data?.message);
            },
            onSuccess: () => {
                toastNotification('success', 'Successfully refreshed calculation!');
            },
        });
    }

    /**
     * Generates a pdf export of the current calculation and immediately downloads its.
     * An event is sent to Intercom based on the result of the export.
     */
    async function exportCalculation(): Promise<void> {
        // function Calc(): JSX.Element {
        //     return (
        //         <Document>
        //             <Page size="A4" style={{ paddingVertical: 64, paddingHorizontal: 48 }}>
        //                 <View style={{ display: 'flex', flexDirection: 'column', paddingBottom: 28 }}>
        //                     <CustomWorksheetSchema calculation={calculation} />
        //                 </View>
        //             </Page>
        //         </Document>
        //     );
        // }
        // const done = asyncToast('Generating pdf export...');
        // try {
        //     // Generate pdf
        //     const blob = await pdf(Calc()).toBlob();
        //     // Trigger a file download using the calculation name as the file name
        //     const url = window.URL.createObjectURL(blob);
        //     const link = document.createElement('a');
        //     link.href = url;
        //     link.setAttribute('download', `${calculation.name}.pdf`);
        //     document.body.appendChild(link);
        //     link.click();
        //     document.body.removeChild(link);
        //     // Record intercom event
        //     trackEvent('calculations-export', {
        //         description: 'User has exported a calculation',
        //         outcome: 'success',
        //         calculationName: calculation.name,
        //         workpaperId: workpaperId,
        //         calculationId: calculation.id,
        //     });
        //     done('success', { title: 'Export generated successfully.' });
        // } catch (error) {
        //     trackEvent('calculations-export', {
        //         description: 'User has failed to export a calculation',
        //         outcome: 'error',
        //         calculationName: calculation.name,
        //         workpaperId: workpaperId,
        //         calculationId: calculation.id,
        //     });
        //     setExportError(true);
        //     done('error', { title: 'Failed to generate the export. Please try again.' });
        // }
    }

    return (
        <div className="flex items-center gap-4 pb-4 border-b">
            {exportError && (
                <Dialog
                    type="error"
                    onClose={(): void => setExportError(false)}
                    isOpen
                    primaryAction="Close"
                    onPrimaryActionClick={(): void => setExportError(false)}
                    title="Unable to export calculation"
                    description={`We couldn't export the calculation: ${calculation.name}. Please refresh the page and try again. If the issue persists, please contact support.`}
                />
            )}
            {reset && (
                <ResetFormConfirmation
                    worksheetId={worksheetId}
                    calculationId={calculation.id}
                    name={name}
                    onClose={(): void => setReset(false)}
                />
            )}
            <TooltipV2 message="Collapse All" position="left">
                <ButtonV2
                    size="xs"
                    type="secondary"
                    leftIcon={<ArrowsPointingInIcon />}
                    onClick={(): void => {
                        dispatch(setCollapseAll(true));
                    }}
                />
            </TooltipV2>
            <TooltipV2 message="Expand All">
                <ButtonV2
                    size="xs"
                    type="secondary"
                    leftIcon={<ArrowsPointingOutIcon />}
                    onClick={(): void => {
                        dispatch(setCollapseAll(false));
                    }}
                />
            </TooltipV2>
            <div className="grow">
                <Search size="small" placeholder="Search fields..." onChange={handleSearch} />
            </div>
            <ButtonV2
                size="xs"
                type="secondary"
                label={refreshQuery.isLoading ? 'Refreshing...' : 'Refresh'}
                leftIcon={<ArrowPathRoundedSquareIcon />}
                isDisabled={refreshQuery.isLoading || (calculation.schema.sources ?? []).length === 0}
                disabledHint="This calculation does not use external sources"
                onClick={refresh}
                isLoading={refreshQuery.isLoading}
            />
            <Dropdown dotsDirection="horizontal" disabled={readOnly}>
                {/* <Dropdown.Option label="Export to PDF" value="EXPORT" onClick={exportCalculation} /> */}
                <Dropdown.Option
                    label="Reset Form"
                    value="RESET"
                    onClick={(): void => setReset(true)}
                    disabledHint={disableResetAction ? 'This requires a reviewer role.' : undefined}
                    disabled={disableResetAction}
                />
                {showHidden ? (
                    <Dropdown.Option
                        label="Hide hidden fields"
                        value="HIDE"
                        onClick={(): void => {
                            dispatch(setShowHidden(false));
                        }}
                    />
                ) : (
                    <Dropdown.Option
                        label="Show hidden fields"
                        value="SHOW"
                        onClick={(): void => {
                            dispatch(setShowHidden(true));
                        }}
                    />
                )}
            </Dropdown>
        </div>
    );
}

export default FormActions;
